import { useState, useEffect, useCallback } from 'react';
import {
  getSubmissionsIndex,
  getSubmissionIndexDetail,
  scanOrphanIndexEntries,
  unlockSubmission,
  type SubmissionIndexEntry,
  type SubmissionIndexDetail,
  type OrphanIndexEntry,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import toast from 'react-hot-toast';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

function toDate(ts: unknown): Date | null {
  if (!ts) return null;
  if (typeof ts === 'object' && 'toDate' in ts && typeof (ts as { toDate: unknown }).toDate === 'function') {
    return (ts as { toDate: () => Date }).toDate();
  }
  if (typeof ts === 'object' && 'seconds' in ts) {
    return new Date((ts as { seconds: number }).seconds * 1000);
  }
  return null;
}

function fmt(ts: unknown): string {
  const d = toDate(ts);
  return d ? d.toLocaleString() : '—';
}

function statusBadge(status: SubmissionIndexEntry['status']) {
  const variant =
    status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning';
  return <Badge variant={variant}>{status}</Badge>;
}

export function SubmissionsIndexManager() {
  const [entries, setEntries] = useState<SubmissionIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [detail, setDetail] = useState<SubmissionIndexDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confirmUnlock, setConfirmUnlock] = useState<{
    contentKey: string;
    status: SubmissionIndexEntry['status'];
    canDeleteRef: boolean;
  } | null>(null);
  const [deleteRefChecked, setDeleteRefChecked] = useState(false);

  const [orphans, setOrphans] = useState<OrphanIndexEntry[] | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    setLoading(true);
    getSubmissionsIndex(statusFilter === 'all' ? undefined : statusFilter)
      .then((rows) => {
        rows.sort((a, b) => (toDate(b.lastUpdated)?.getTime() ?? 0) - (toDate(a.lastUpdated)?.getTime() ?? 0));
        setEntries(rows);
      })
      .catch((err) => {
        console.error('Failed to load submissions index:', err);
        toast.error('Failed to load submissions index');
      })
      .finally(() => setLoading(false));
  }, [statusFilter, refreshKey]);

  const filtered = entries.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.contentKey.toLowerCase().includes(q) ||
      (e.brandId ?? '').toLowerCase().includes(q) ||
      (e.referenceNormalized ?? '').toLowerCase().includes(q)
    );
  });

  const openDetail = useCallback(async (contentKey: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await getSubmissionIndexDetail(contentKey);
      setDetail(d);
    } catch (err) {
      console.error('Failed to load detail:', err);
      toast.error('Failed to load entry detail');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleScanOrphans = async () => {
    setScanning(true);
    try {
      const found = await scanOrphanIndexEntries();
      setOrphans(found);
      toast.success(found.length ? `${found.length} orphan(s) found` : 'No orphans found');
    } catch (err) {
      console.error('Orphan scan failed:', err);
      toast.error('Orphan scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleUnlock = async () => {
    if (!confirmUnlock) return;
    setProcessing(true);
    try {
      const res = await unlockSubmission(
        confirmUnlock.contentKey,
        confirmUnlock.canDeleteRef && deleteRefChecked,
      );
      toast.success(
        res.deletedCatalogueRef
          ? 'Unlocked and deleted catalogue ref'
          : 'Unlocked back to pending',
      );
      setConfirmUnlock(null);
      setDeleteRefChecked(false);
      setDetail(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Unlock failed:', err);
      toast.error('Unlock failed — see console');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Submissions Index</h1>
          <p className="text-gray-500 mt-1">
            {entries.length} {statusFilter === 'all' ? 'entries' : `${statusFilter} entries`}
            {' · '}dedup ledger for user reference submissions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleScanOrphans} loading={scanning}>
            Scan orphans
          </Button>
          <Button variant="secondary" onClick={() => setRefreshKey((k) => k + 1)}>
            Refresh
          </Button>
        </div>
      </div>

      {orphans && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-amber-900">
              Orphan scan: {orphans.length} flagged
            </h2>
            <button className="text-xs text-amber-700 underline" onClick={() => setOrphans(null)}>
              dismiss
            </button>
          </div>
          {orphans.length === 0 ? (
            <p className="text-sm text-amber-800">No orphaned index entries.</p>
          ) : (
            <ul className="space-y-1 text-xs font-mono text-amber-900">
              {orphans.map((o) => (
                <li key={o.contentKey}>
                  <button className="underline" onClick={() => openDetail(o.contentKey)}>
                    {o.contentKey.slice(0, 12)}…
                  </button>{' '}
                  · {o.brandId}/{o.referenceNormalized} · {o.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <input
          type="text"
          placeholder="Search by contentKey, brand, or reference…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand / Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current submission</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((e) => (
                  <tr key={e.contentKey} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {e.brandId} / {e.referenceNormalized}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">{e.contentKey.slice(0, 16)}…</div>
                    </td>
                    <td className="px-4 py-3">{statusBadge(e.status)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                      {e.currentSubmissionId || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{fmt(e.lastUpdated)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(e.contentKey)}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12 text-gray-500">No matching entries</div>
            )}
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Modal
        isOpen={detailLoading || !!detail}
        onClose={() => setDetail(null)}
        title="Index Entry"
        size="lg"
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
          </div>
        ) : detail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="contentKey" value={detail.entry.contentKey} mono />
              <Field label="status" value={detail.entry.status} />
              <Field label="brandId" value={detail.entry.brandId ?? '—'} />
              <Field label="referenceNormalized" value={detail.entry.referenceNormalized ?? '—'} />
              <Field label="currentSubmissionId" value={detail.entry.currentSubmissionId ?? '—'} mono />
              <Field label="firstSubmittedBy" value={detail.entry.firstSubmittedBy ?? '—'} mono />
              <Field label="firstSubmittedAt" value={fmt(detail.entry.firstSubmittedAt)} />
              <Field label="lockedAt" value={fmt(detail.entry.lockedAt)} />
              <Field label="approvedBrandId" value={detail.entry.approvedBrandId ?? '—'} />
              <Field label="approvedReferenceId" value={detail.entry.approvedReferenceId ?? '—'} mono />
            </div>

            <div className="border-t pt-3 text-sm space-y-1">
              <div>
                <span className="text-gray-500">Linked pending doc: </span>
                {detail.pending ? (
                  <span className="text-gray-900">
                    {detail.pending.brandName} {detail.pending.reference} ({detail.pending.status})
                  </span>
                ) : (
                  <span className="text-gray-400">none</span>
                )}
              </div>
              {(detail.entry.approvedReferenceId || detail.entry.status === 'approved') && (
                <div>
                  <span className="text-gray-500">Catalogue ref: </span>
                  <span className={detail.catalogueRefExists ? 'text-green-700' : 'text-red-600'}>
                    {detail.catalogueRefExists ? 'exists' : 'missing'}
                  </span>
                </div>
              )}
            </div>

            {(detail.entry.status === 'approved' || detail.entry.status === 'rejected') && (
              <div className="border-t pt-4 flex justify-end">
                <Button
                  variant="danger"
                  onClick={() =>
                    setConfirmUnlock({
                      contentKey: detail.entry.contentKey,
                      status: detail.entry.status,
                      canDeleteRef:
                        detail.entry.status === 'approved' &&
                        !!detail.entry.approvedReferenceId,
                    })
                  }
                >
                  Unlock submission
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      {/* Unlock confirmation */}
      <Modal
        isOpen={!!confirmUnlock}
        onClose={() => {
          setConfirmUnlock(null);
          setDeleteRefChecked(false);
        }}
        title="Unlock submission"
        size="sm"
      >
        {confirmUnlock && (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              This unlocks the <strong>{confirmUnlock.status}</strong> entry back to{' '}
              <strong>pending</strong>, re-opening this brand + reference for submission.
            </p>
            {confirmUnlock.canDeleteRef && (
              <label className="flex items-start gap-2 cursor-pointer bg-red-50 border border-red-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  checked={deleteRefChecked}
                  onChange={(e) => setDeleteRefChecked(e.target.checked)}
                  className="mt-0.5 rounded text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-red-700">
                  Also delete the catalogue reference that was created on approval.
                  This removes it from the public catalogue (and Algolia).
                </span>
              </label>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmUnlock(null);
                  setDeleteRefChecked(false);
                }}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleUnlock} loading={processing}>
                {deleteRefChecked ? 'Unlock + delete ref' : 'Unlock'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-gray-400 uppercase">{label}</div>
      <div className={`text-gray-900 break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}
